/**
 * Сесийната част на резерва (§8 на договора, docs/edge.md §Сесия): при
 * всяка смяна на активния токен (вход, ПИН вход на общ терминал, изход на
 * служителя) дърпа `GET /configuration/edge`, издава билет
 * (`POST /user/edge-ticket`) и прави ЕДНА сонда `GET {edge}/health`, която
 * вдига prompt-а „Local Network Access" на Chrome; конфигурацията се
 * опреснява на 5 min, билетът — на 6 h. Режимът и сондите с хистерезис са
 * в `edgeMode.ts`, който вика `handleTokenChange`/`restartSession` оттук.
 *
 * Тук е и `prepareThisDevice()` — същите стъпки, но пуснати ОТ ЧОВЕК: само
 * така Chromium вдига въпроса за локалната мрежа.
 */
import {
  getEdgeHealth,
  issueEdgeTicket,
  type EdgeHealthOut,
} from "../../api/edge";
import {
  setEdgeReachable,
  setReservationsOffline,
  setThisDeviceIsTill,
  setTipsOffline,
  setBillDiscountOffline,
} from "../../store/edgeSlice";
import { fetchEdgeConfig } from "./edgeConfig";
import {
  forgetTillOnThisDevice,
  mayLookForTillOnThisDevice,
  probeTillOnThisDevice,
} from "./edgeLoopback";
import {
  localNetworkPermissionState,
  noteEdgeProbeResult,
} from "./edgePermission";
import {
  startEdgeReservationsFeed,
  stopEdgeReservationsFeed,
} from "./edgeReservationsFeed";
import { warmEdgeMirror } from "./edgeWarmup";
import {
  applyConfig,
  currentToken,
  devLog,
  getEdgeRuntime,
} from "./edgeRuntime";
import {
  EDGE_LOOPBACK_HOST,
  getEdgeBaseUrl,
  getEdgeConfigState,
  getEdgeLanBaseUrl,
  getEdgeLoopbackBaseUrl,
  isEdgeLocalMode,
  isPrivateLanAddress,
  isThisDeviceTheTill,
} from "./edgeState";
import {
  hasCloudEdgeTicket,
  isLocalEdgeToken,
  storeEdgeTicket,
} from "./edgeTicket";

const CONFIG_REFRESH_INTERVAL_MS = 5 * 60_000;
const TICKET_REFRESH_INTERVAL_MS = 6 * 60 * 60_000;

let sessionSerial = 0;
let configTimer: number | null = null;
let ticketTimer: number | null = null;

/** Нов активен токен (или никакъв): старите таймери спират, тръгва нова сесия. */
export function handleTokenChange(token: string | null) {
  stopSessionTimers();
  sessionSerial += 1;
  if (!token) return;
  void startSession(token, sessionSerial);
}

/** Същият токен, нова сесия — при връщане от локален режим билетът и
 * конфигурацията са от преди прекъсването и се опресняват. */
export function restartSession() {
  const token = currentToken();
  if (!token) return;
  sessionSerial += 1;
  void startSession(token, sessionSerial);
}

async function startSession(token: string, serial: number) {
  // В ЛОКАЛЕН режим не се пита за конфигурация ИЗОБЩО: заявката отива към
  // касата, тя връща каквото е огледала някога, а `fetchEdgeConfig` записва
  // отговора и в кеша `edge_config`. Един такъв отговор без адрес остава там
  // и след презареждане — терминалът тръгва „с изключен резерв" и вече няма
  // как да намери касата. Конфигурацията се опреснява, когато облакът се
  // върне (`edgeMode.leaveLocalMode` → `restartSession`).
  if (!isEdgeLocalMode()) {
    const fetched = await fetchEdgeConfig();
    if (serial !== sessionSerial) return;
    if (fetched) applyConfig(fetched);
  }
  startConfigTimer();
  // Изгледът на резервациите към касата. Тръгва ПРЕДИ проверката за
  // включен резерв: таймерът сам гледа условията (облак, включен резерв,
  // способност на касата, право, видим таб) при всеки удар, а резерв,
  // включен по-късно от портала, не пуска нова сесия.
  startEdgeReservationsFeed();

  if (!getEdgeConfigState()?.enabled) return;
  if (!isEdgeLocalMode()) {
    await refreshTicket(token);
    if (serial !== sessionSerial) return;
    const reachable = await probeEdgeReachability();
    // §14.6 №5: касата сервира офлайн само видяното — един тих GET по
    // белите пътища, които още не са огледани (`edgeWarmup.ts`).
    if (reachable && serial === sessionSerial) void warmEdgeMirror();
  }
  startTicketTimer();
}

async function refreshTicket(token: string) {
  // Локален билет не се подновява от облака — той изтича с връщането.
  if (isLocalEdgeToken(token)) return;
  try {
    const issued = await issueEdgeTicket();
    // Пази се под токена, с който е ТРЪГНАЛА заявката — дори междувременно
    // да е влязъл друг служител, билетът е валиден за този потребител.
    storeEdgeTicket(token, issued.ticket, issued.expires_at);
  } catch (error) {
    // Стар BE без endpoint-а или без интернет — кешираният билет остава.
    devLog("ticket refresh failed", error);
  }
}

function startConfigTimer() {
  if (configTimer !== null) return;
  configTimer = window.setInterval(
    () => void refreshConfigFromTimer(),
    CONFIG_REFRESH_INTERVAL_MS,
  );
}

async function refreshConfigFromTimer() {
  // В ЛОКАЛЕН режим този `GET /configuration/edge` не идва от облака, а от
  // ОГЛЕДАЛОТО на касата — последният запомнен отговор, понякога отпреди
  // включването на резерва. Прилагането му изтриваше адреса на касата
  // (`ip: null`) и терминалът оставаше в локален режим без към кого да се
  // обърне: `getEdgeBaseUrl()` връща null, сондите спират, а връщането в
  // облака минава „без проверка" — точно през дневник, който не е изпратен.
  // Затова първо проверката, чак после заявката.
  if (isEdgeLocalMode()) return;
  const before = getEdgeConfigState();
  const fetched = await fetchEdgeConfig();
  if (!fetched) return;
  applyConfig(fetched);
  if (!fetched.enabled) return;
  const token = currentToken();
  if (token && !hasCloudEdgeTicket(token)) await refreshTicket(token);
  // Току-що включен резерв или сменен адрес на касата → нова сонда (и prompt).
  if (
    !before?.enabled ||
    before.ip !== fetched.ip ||
    before.port !== fetched.port
  ) {
    await probeEdgeReachability();
  } else {
    await recheckEdgeCapabilities();
  }
  startTicketTimer();
}

/**
 * Каса, която стига, но още не умее резервации или бакшиш: обновена каса
 * (или BE, който едва сега пусна флага) иначе не се вижда до презареждане, а
 * терминалите в заведението рядко се презареждат. Чете САМО способностите —
 * не пипа `edgeReachable` и не закача слушането за разрешение: бавен или
 * пропаднал отговор тук не бива да показва касата като недостъпна. Вече
 * известна способност не се сваля оттук (сондата за достъпност я сваля).
 */
async function recheckEdgeCapabilities() {
  const runtime = getEdgeRuntime();
  const edge = runtime?.store.getState().edge;
  if (edge?.edgeReachable !== true) return;
  const needsReservations = edge.reservationsOffline !== true;
  const needsTips = edge.tipsOffline !== true;
  const needsBillDiscount = edge.billDiscountOffline !== true;
  if (!needsReservations && !needsTips && !needsBillDiscount) return;
  const base = getEdgeBaseUrl();
  if (!base) return;
  try {
    const health = await getEdgeHealth(base);
    if (health.ok !== true || health.enabled !== true) return;
    if (needsTips && health.capabilities?.tips === 1) {
      runtime?.store.dispatch(setTipsOffline(true));
    }
    if (needsBillDiscount && health.capabilities?.bill_discount === 1) {
      runtime?.store.dispatch(setBillDiscountOffline(true));
    }
    if (needsReservations && health.capabilities?.reservations === 1) {
      runtime?.store.dispatch(setReservationsOffline(true));
      // Касата току-що се е научила на резервации → първото пълнене на
      // изгледа и схемата (подгряването пропуска вече огледаното).
      void warmEdgeMirror();
    }
  } catch (error) {
    devLog("edge capability recheck failed", error);
  }
}

function startTicketTimer() {
  if (ticketTimer !== null) return;
  ticketTimer = window.setInterval(() => {
    const token = currentToken();
    if (token && !isEdgeLocalMode() && getEdgeConfigState()?.enabled) {
      void refreshTicket(token);
    }
  }, TICKET_REFRESH_INTERVAL_MS);
}

function stopSessionTimers() {
  stopEdgeReservationsFeed();
  if (configTimer !== null) {
    clearInterval(configTimer);
    configTimer = null;
  }
  if (ticketTimer !== null) {
    clearInterval(ticketTimer);
    ticketTimer = null;
  }
}

/**
 * Стига ли браузърът до касата — `GET {edge}/health` (timeout 3 s). Първото
 * извикване от HTTPS страница вдига prompt-а „Local Network Access" на
 * Chrome/Edge; резултатът стои в `edgeSlice.edgeReachable` и се показва в
 * Конфигурация → Устройства → „Локален резерв". Вика се при вход, от
 * бутона „Провери" на реда и от `edgeMode.ts` преди всяко влизане в
 * локален режим. Едновременните извиквания споделят една заявка.
 *
 * `humanAction` казва, че сондата тръгва от изричен бутон („Подготви",
 * „Провери") — само тогава при пропаднал LAN път се пробва и касата на този
 * компютър (`edgeLoopback.ts`). Без него поведението на устройство в залата е
 * същото като преди.
 */
let reachabilityProbe: Promise<boolean> | null = null;

/** Лети ли сонда в момента. Пита `edgePermission.ts`: сонда, тръгнала извън
 * човешко действие, не вдига подканата, а споделеното обещание би скрило това
 * от кликналия — затова тогава изчакваме следващото докосване. */
export function isReachabilityProbeRunning(): boolean {
  return reachabilityProbe !== null;
}

export function probeEdgeReachability(options?: {
  humanAction?: boolean;
}): Promise<boolean> {
  if (reachabilityProbe) return reachabilityProbe;
  reachabilityProbe = runReachabilityProbe(
    options?.humanAction === true,
  ).finally(() => {
    reachabilityProbe = null;
  });
  return reachabilityProbe;
}

const isServing = (health: EdgeHealthOut | null): health is EdgeHealthOut =>
  health?.ok === true && health.enabled === true;

/** Отговорът на касата или null, ако нищо не отговори (мрежа, таймаут, 503 —
 * axios хвърля за не-2xx, както и досега). */
async function readTillHealth(
  base: string | null,
): Promise<EdgeHealthOut | null> {
  if (!base) return null;
  try {
    return await getEdgeHealth(base);
  } catch (error) {
    devLog("edge health probe failed", error);
    return null;
  }
}

/**
 * Пътищата към касата, в този ред:
 *  1. `127.0.0.1` — ако устройството вече знае, че е самата каса;
 *  2. обявеният LAN адрес — както винаги; отговори ли там касата, след като
 *     т. 1 е мълчала, запомненото се забравя и заявките тръгват по LAN;
 *  3. `127.0.0.1` като проверка „дали това не е касата" — само когато
 *     `mayLookForTillOnThisDevice` го позволи.
 * Връща първия отговор, който обслужва, иначе първия получен (за
 * способностите), или null, ако нищо не отговори.
 */
async function findTill(humanAction: boolean): Promise<EdgeHealthOut | null> {
  const viaLoopback = isThisDeviceTheTill();
  let health: EdgeHealthOut | null = null;
  if (viaLoopback) health = await readTillHealth(getEdgeLoopbackBaseUrl());
  if (isServing(health)) return health;

  const lanHealth = await readTillHealth(getEdgeLanBaseUrl());
  if (isServing(lanHealth)) {
    // Тук мълчи, а по LAN отговаря: касата е преместена на друга машина или
    // пътят през `127.0.0.1` е спрян (отнето разрешение). И в двата случая
    // заявките трябва да тръгнат по пътя, който отговори — иначе сондата
    // казва „стига", а `getEdgeBaseUrl()` още води към мълчащия.
    if (viaLoopback) forgetTillOnThisDevice();
    return lanHealth;
  }
  health = health ?? lanHealth;

  if (!viaLoopback && mayLookForTillOnThisDevice(humanAction)) {
    const hereHealth = await probeTillOnThisDevice();
    if (hereHealth) return hereHealth;
  }
  return health;
}

async function runReachabilityProbe(humanAction: boolean): Promise<boolean> {
  const runtime = getEdgeRuntime();
  if (!getEdgeBaseUrl() && !mayLookForTillOnThisDevice(humanAction)) {
    runtime?.store.dispatch(
      setEdgeReachable(getEdgeConfigState()?.enabled ? false : null),
    );
    runtime?.store.dispatch(setReservationsOffline(false));
    runtime?.store.dispatch(setTipsOffline(false));
    runtime?.store.dispatch(setBillDiscountOffline(false));
    return false;
  }
  const health = await findTill(humanAction);
  runtime?.store.dispatch(setThisDeviceIsTill(isThisDeviceTheTill()));
  if (!health) {
    runtime?.store.dispatch(setEdgeReachable(false));
    // Провалът закача слушане за първото докосване: така подканата на
    // браузъра идва при обичайната работа, вместо да чака някой да обиколи
    // устройствата (`edgePermission.ts`). Мълчаща каса не сваля вече
    // известните способности — те са от последния ОТГОВОР.
    noteEdgeProbeResult(false);
    return false;
  }
  const reachable = isServing(health);
  runtime?.store.dispatch(setEdgeReachable(reachable));
  // Способността се чете при ВСЯКА сонда, не само при вход: всички пътища
  // към локален режим (студен старт, 423, хистерезис) минават оттук, а
  // по-стара каса без полето значи „резервациите остават заключени".
  runtime?.store.dispatch(
    setReservationsOffline(
      reachable && health.capabilities?.reservations === 1,
    ),
  );
  // Бакшишът без интернет — същото правило (`capabilities.tips`).
  runtime?.store.dispatch(
    setTipsOffline(reachable && health.capabilities?.tips === 1),
  );
  // Отстъпката в € на цялата сметка без интернет — същото правило.
  runtime?.store.dispatch(
    setBillDiscountOffline(
      reachable && health.capabilities?.bill_discount === 1,
    ),
  );
  noteEdgeProbeResult(reachable);
  return reachable;
}

/**
 * Изходът на „Подготви това устройство" — по един за всяко място, където
 * човекът трябва да отиде след това.
 */
export type EdgePrepareOutcome =
  | "ready"
  | "blocked"
  | "unreachable"
  | "publicAddress"
  | "disabled"
  | "noMaster"
  | "localNow";

/**
 * Какво чува човекът за всеки изход. Стои тук, а не в двата компонента, защото
 * бутонът е на ДВЕ места (Конфигурация → Устройства и лентата на монитора), а
 * най-важната фраза — „браузърът отказа достъп до локалната мрежа" — трябва да
 * е една и съща, където и да я срещне.
 */
export const EDGE_PREPARE_FEEDBACK: Record<
  EdgePrepareOutcome,
  { key: string; tone: "success" | "error" | "warning" }
> = {
  ready: { key: "edge.prepareReady", tone: "success" },
  blocked: { key: "edge.prepareBlocked", tone: "error" },
  unreachable: { key: "edge.prepareUnreachable", tone: "error" },
  publicAddress: { key: "edge.preparePublicAddress", tone: "warning" },
  disabled: { key: "edge.prepareDisabled", tone: "warning" },
  noMaster: { key: "edge.prepareNoMaster", tone: "warning" },
  localNow: { key: "edge.prepareLocalNow", tone: "warning" },
};

/** Параметрите на текстовете от `EDGE_PREPARE_FEEDBACK` — адресът, на който
 * ТОВА устройство търси касата (`127.0.0.1`, ако то е самата каса). */
export function edgePrepareMessageParams(): { address: string } {
  const config = getEdgeConfigState();
  if (!config) return { address: "—" };
  if (isThisDeviceTheTill()) {
    return { address: `${EDGE_LOOPBACK_HOST}:${config.port}` };
  }
  return { address: config.ip ? `${config.ip}:${config.port}` : "—" };
}

/**
 * Защо касата не отговори — за да не обвиняваме браузъра, когато вината е
 * другаде. „Отказа достъп" само ако браузърът наистина казва `denied`;
 * публичен адрес — отделно, защото там нито разрешение, нито рестарт помагат;
 * всичко останало (изгасена каса, грешен/стар адрес, firewall) е „не отговаря
 * на {адрес}" — с адреса, за да се види веднага, ако е грешният.
 */
async function whyTillUnreachable(): Promise<EdgePrepareOutcome> {
  if ((await localNetworkPermissionState()) === "denied") return "blocked";
  const ip = getEdgeConfigState()?.ip;
  if (!isThisDeviceTheTill() && ip && !isPrivateLanAddress(ip)) {
    return "publicAddress";
  }
  return "unreachable";
}

/**
 * „Подготви това устройство" — бутонът от Конфигурация → Устройства и от
 * лентата на монитора.
 *
 * Chromium дава разрешението за локална мрежа САМО при действие на човек:
 * сондата, която приложението пуска само̀ при вход, не вдига подканата, затова
 * устройство без явно натискане остава без разрешение и мълчи чак когато
 * интернетът падне — най-лошият възможен момент да го откриеш. Затова всичко,
 * което едно устройство иска, се случва в рамките на този клик: пресни
 * настройки (резервът може да е включен преди минута от друг терминал), пресен
 * билет, сондата (тя вдига подканата) и подгряването на огледалото, за да има
 * какво да сервира касата.
 *
 * Подгряването се ИЗЧАКВА нарочно — отговорът „това устройство ще работи
 * офлайн" трябва да е истина в мига, в който човекът го прочете, а не обещание.
 */
export async function prepareThisDevice(): Promise<EdgePrepareOutcome> {
  const fetched = await fetchEdgeConfig();
  if (fetched) applyConfig(fetched);

  if (!getEdgeConfigState()?.enabled) return "disabled";
  // Вече сме срещу касата — устройството е доказало, че стига до нея, а
  // огледалото се пълни от самата работа.
  if (isEdgeLocalMode()) return "localNow";
  if (!getEdgeBaseUrl() && !mayLookForTillOnThisDevice(true)) {
    return "noMaster";
  }

  const token = currentToken();
  if (token) await refreshTicket(token);
  if (!(await probeEdgeReachability({ humanAction: true }))) {
    return whyTillUnreachable();
  }
  await warmEdgeMirror();
  startTicketTimer();
  return "ready";
}

/** След `PUT /configuration/edge` от панела: нова конфигурация, билет, сонда. */
export async function refreshEdgeSettings(): Promise<void> {
  // Същата причина като в `refreshConfigFromTimer`: срещу касата отговорът е
  // от огледалото и не бива нито да се прилага, нито да се кешира.
  if (isEdgeLocalMode()) return;
  const fetched = await fetchEdgeConfig();
  if (fetched) applyConfig(fetched);
  const token = currentToken();
  if (!getEdgeConfigState()?.enabled || !token) return;
  await refreshTicket(token);
  if (await probeEdgeReachability({ humanAction: true })) {
    void warmEdgeMirror();
  }
  startTicketTimer();
}
